import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import SignalCellular4BarIcon from "@mui/icons-material/SignalCellular4Bar";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import SignalCellularOffIcon from "@mui/icons-material/SignalCellularOff";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";

const PopoverPaper = styled(Popover)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 12,
    minWidth: 240,
    maxWidth: 320,
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    padding: theme.spacing(1),
  },
}));

const getChannelStatus = (whatsApp) => {
  if (whatsApp.status === "CONNECTED") return "connected";
  if (whatsApp.status === "OPENING") return "opening";
  if (whatsApp.qrcode) return "qrcode";
  return "disconnected";
};

const statusChipProps = {
  connected: { color: "success" },
  opening: { color: "info" },
  qrcode: { color: "warning" },
  disconnected: { color: "error" },
};

const ChannelStatusIcon = () => {
  const { t } = useTranslation();
  const { whatsApps } = useContext(WhatsAppsContext);
  const [anchorEl, setAnchorEl] = useState(null);

  const total = whatsApps?.length ?? 0;
  const connected = whatsApps?.filter((w) => w.status === "CONNECTED").length ?? 0;

  let aggregated;
  if (total === 0 || connected === 0) {
    aggregated = "all_disconnected";
  } else if (connected === total) {
    aggregated = "all_connected";
  } else {
    aggregated = "partial";
  }

  let tooltipTitle;
  if (aggregated === "all_connected") {
    tooltipTitle = t("mainDrawer.appBar.channelStatus.allConnected", { count: total });
  } else if (aggregated === "partial") {
    tooltipTitle = t("mainDrawer.appBar.channelStatus.partial", { connected, total });
  } else {
    tooltipTitle = t("mainDrawer.appBar.channelStatus.noneConnected");
  }

  const iconSx = { fontSize: 22 };

  const icon =
    aggregated === "all_connected" ? (
      <SignalCellular4BarIcon sx={{ ...iconSx, color: "inherit" }} />
    ) : aggregated === "partial" ? (
      <SignalCellularAltIcon sx={{ ...iconSx, color: "inherit" }} />
    ) : (
      <SignalCellularOffIcon sx={{ ...iconSx, color: "inherit" }} />
    );

  const buttonColor =
    aggregated === "all_connected"
      ? "success.light"
      : aggregated === "partial"
      ? "warning.light"
      : "error.light";

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          size="small"
          sx={{
            color: buttonColor,
            borderRadius: "50%",
            padding: 0.75,
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          {icon}
        </IconButton>
      </Tooltip>

      <PopoverPaper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Typography
          variant="subtitle2"
          sx={{ px: 1.5, pt: 1, pb: 0.5, fontWeight: 600 }}
        >
          {tooltipTitle}
        </Typography>

        <List dense disablePadding>
          {whatsApps?.map((w) => {
            const s = getChannelStatus(w);
            const label = t(`mainDrawer.appBar.channelStatus.${s}`);
            return (
              <ListItem key={w.id} sx={{ py: 0.5 }}>
                <ListItemText
                  primary={w.name}
                  primaryTypographyProps={{ variant: "body2", noWrap: true }}
                />
                <ListItemSecondaryAction>
                  <Chip
                    label={label}
                    size="small"
                    {...statusChipProps[s]}
                    sx={{ fontSize: "0.7rem", height: 22 }}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
          {(!whatsApps || whatsApps.length === 0) && (
            <ListItem>
              <ListItemText
                primary={t("mainDrawer.appBar.channelStatus.noneConnected")}
                primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
              />
            </ListItem>
          )}
        </List>
      </PopoverPaper>
    </>
  );
};

export default ChannelStatusIcon;
